/**
 * The one recency-ordered session list `HistoryDrawer` renders:
 * lane-v2 sessions (`GET /api/sessions`, R6) —
 * fetched only when R0 advertises `session_list` (otherwise the list is empty and `hasV2` is false).
 *
 * The sort/filter rule is pure (`laneV2/sessionListing.ts`); this hook wires the source and the
 * "show closed" toggle.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { resolveActiveOrgId, useAuthStoreV1 } from "@/stores/authStore_v1";
import { getSessions } from "@/services/multi-agent/laneV2/api";
import { resolveCapabilities, subscribeCapabilitiesRecovered } from "@/services/multi-agent/laneV2/lane";
import { sessionRows, type MergedSessionRow } from "@/services/multi-agent/laneV2/sessionListing";

export interface UseMergedSessionsOptions {
  enabled?: boolean;
}

export interface UseMergedSessionsResult {
  rows: MergedSessionRow[];
  /** Whether R0 advertised `session_list` for this org — surfaces use this to decide whether the
   *  "show closed" toggle is worth rendering at all. */
  hasV2: boolean;
  showClosed: boolean;
  setShowClosed: (v: boolean) => void;
  refetch: () => void;
}

export function useMergedSessions(options: UseMergedSessionsOptions = {}): UseMergedSessionsResult {
  const { enabled = true } = options;
  // The same "actively selected org, single-org fallback" rule `client.ts` and `useLaneV2Route.ts`
  // use (`authStore_v1.ts`'s `resolveActiveOrgId`). Before this fix this read `selectedOrg?.id` alone,
  // so a single-org user with no `selectedOrg` yet got v2 new chats from `useLaneV2Route` but never
  // saw v2 sessions here: `enabled: !!orgId` below was false, so the capabilities/session-list
  // queries never ran at all.
  const orgId = useAuthStoreV1((s) => resolveActiveOrgId(s));
  const [showClosed, setShowClosed] = useState(false);

  const capsQuery = useQuery({
    queryKey: ["lane-v2", "capabilities", orgId ?? ""],
    queryFn: async () => {
      const caps = await resolveCapabilities(orgId);
      // An unreachable R0 is not an answer. The ONE re-probe loop lives in `useLaneV2Route`; this query refetches when it recovers.
      if (caps.connecting) throw new Error("capabilities unreachable");
      return caps;
    },
    enabled: enabled && !!orgId,
    staleTime: 60_000,
    retry: false,
  });
  const refetchCaps = capsQuery.refetch;
  useEffect(() => subscribeCapabilitiesRecovered(() => void refetchCaps()), [refetchCaps]);
  const hasV2 = capsQuery.data?.sessionList === true;

  // ALWAYS `includeClosed: true`: one query, `showClosed` toggles rendering only, not the fetch.
  const v2Query = useQuery({
    queryKey: ["lane-v2", "sessions", orgId ?? ""],
    queryFn: () => getSessions({ includeClosed: true, limit: 100 }),
    enabled: enabled && !!orgId && hasV2,
  });

  const rows = useMemo(
    () => sessionRows(v2Query.data?.sessions ?? [], { showClosed }),
    [v2Query.data, showClosed],
  );

  return {
    rows,
    hasV2,
    showClosed,
    setShowClosed,
    refetch: () => {
      if (hasV2) void v2Query.refetch();
    },
  };
}
